import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Clock, Users, CheckCircle2, X, Eye } from 'lucide-react';
import { LoadingSpinner } from './core';
import { API } from './shared';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

export function RelanceTab({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/relance/overview`, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData({ stages: [], stats: {}, recent: [] }));
  }, []);

  if (!data) return <LoadingSpinner />;

  const card = light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/5';
  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 ${light ? 'text-gray-400' : 'text-white/40'}`;
  const td = `px-3 py-2.5 text-sm ${light ? 'text-gray-700' : 'text-white/80'}`;

  return (
    <div className="space-y-6" data-testid="relance-tab">
      <div>
        <h2 className={`text-lg font-bold flex items-center gap-2 ${light ? 'text-gray-900' : 'text-white'}`}>
          <Mail size={18} className="text-[#E9641A]" /> Relances panier abandonné
        </h2>
        <p className={`text-sm mt-1 ${light ? 'text-gray-500' : 'text-white/50'}`}>
          Emails automatiques envoyés aux comptes créés sans abonnement finalisé — 3 étapes : 24 h, 3 jours puis 7 jours.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 max-w-lg">
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${card}`} data-testid="relance-pending-card">
          <Users size={20} className="text-[#E9641A]" />
          <div>
            <p className={`text-xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>{data.pending_users ?? 0}</p>
            <p className={`text-xs ${light ? 'text-gray-500' : 'text-white/50'}`}>Comptes en attente (&lt; 30 j)</p>
          </div>
        </div>
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${card}`} data-testid="relance-converted-card">
          <CheckCircle2 size={20} className="text-emerald-500" />
          <div>
            <p className={`text-xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>{data.converted_after_relance ?? 0}</p>
            <p className={`text-xs ${light ? 'text-gray-500' : 'text-white/50'}`}>Convertis après relance</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data.stages.map(s => {
          const st = data.stats?.[s.stage] || {};
          return (
            <div key={s.stage} className={`rounded-xl border p-5 ${card}`} data-testid={`relance-stage-${s.stage}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold px-2 py-1 rounded-full bg-[#E9641A]/10 text-[#E9641A]">Étape {s.stage}</span>
                <span className={`text-xs flex items-center gap-1 ${light ? 'text-gray-500' : 'text-white/50'}`}><Clock size={12} /> après {s.delay_label}</span>
              </div>
              <p className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>{s.subject}</p>
              <div className={`text-xs space-y-1 mb-4 ${light ? 'text-gray-500' : 'text-white/50'}`}>
                <p>Envoyés : <b className={light ? 'text-gray-800' : 'text-white'}>{st.count || 0}</b></p>
                <p>Dernier envoi : {fmtDate(st.last_sent)}</p>
              </div>
              <button
                data-testid={`relance-preview-btn-${s.stage}`}
                onClick={() => setPreview(s)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white transition-colors">
                <Eye size={14} /> Aperçu de l'email
              </button>
            </div>
          );
        })}
      </div>

      <div className={`rounded-xl border p-5 ${card}`} data-testid="relance-recent-card">
        <p className={`text-sm font-bold mb-4 ${light ? 'text-gray-800' : 'text-white'}`}>Derniers envois</p>
        {(data.recent || []).length === 0 ? (
          <p className={`text-sm ${light ? 'text-gray-400' : 'text-white/40'}`}>Aucune relance envoyée pour le moment.</p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead><tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
                <th className={th}>Email</th><th className={th}>Étape</th><th className={th}>Envoyé le</th>
              </tr></thead>
              <tbody>
                {data.recent.map((r, i) => (
                  <tr key={i} className={light ? 'border-b border-gray-100' : 'border-b border-white/5'}>
                    <td className={`${td} font-medium`}>{r.email}</td>
                    <td className={td}><span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#E9641A]/10 text-[#E9641A]">Étape {r.stage}</span></td>
                    <td className={td}>{fmtDate(r.sent_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <div className={`rounded-xl overflow-hidden w-[560px] max-w-[92vw] max-h-[88vh] flex flex-col ${light ? 'bg-white' : 'bg-[#1a0e2e] border border-white/10'}`}
            onClick={e => e.stopPropagation()} data-testid="relance-preview-modal">
            <div className={`flex items-center justify-between px-5 py-3 border-b ${light ? 'border-gray-200' : 'border-white/10'}`}>
              <div>
                <p className={`text-sm font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Étape {preview.stage} — après {preview.delay_label}</p>
                <p className={`text-xs ${light ? 'text-gray-500' : 'text-white/50'}`}>Objet : {preview.subject}</p>
              </div>
              <button onClick={() => setPreview(null)} data-testid="relance-preview-close" className={light ? 'text-gray-400 hover:text-gray-800' : 'text-white/40 hover:text-white'}><X size={18} /></button>
            </div>
            <iframe title="Aperçu email" srcDoc={preview.html} className="w-full flex-1 min-h-[420px] bg-[#11041F]" sandbox="" />
          </div>
        </div>
      )}
    </div>
  );
}
